import { useState } from "react";
import { startOfDay, subDays } from "date-fns";
import { useGetList, useTranslate } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import type { Lead } from "../types";
import { LeadKpiTiles } from "./LeadKpiTiles";
import { computeKpis, formatDuration, groupKpis } from "./leadUtils";

const PERIODS = [7, 30, 90];

const KpiTable = ({
  title,
  rows,
  fallbackLabel,
}: {
  title: string;
  rows: ReturnType<typeof groupKpis>;
  fallbackLabel: string;
}) => {
  const translate = useTranslate();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{translate(title)}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{translate(fallbackLabel)}</TableHead>
                <TableHead className="text-right">
                  {translate("crm.kpi.total")}
                </TableHead>
                <TableHead className="text-right">
                  {translate("crm.kpi.response_median")}
                </TableHead>
                <TableHead className="text-right">
                  {translate("crm.kpi.claim_median")}
                </TableHead>
                <TableHead className="text-right">
                  {translate("crm.kpi.reached_rate")}
                </TableHead>
                <TableHead className="text-right">
                  {translate("crm.kpi.appointment_rate")}
                </TableHead>
                <TableHead className="text-right">
                  {translate("crm.kpi.close_rate")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell>{row.key}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.total}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatDuration(row.responseMedian)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatDuration(row.claimMedian)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.reachedRate} %
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.appointmentRate} %
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.closeRate} %
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export const LeadKpiPage = () => {
  const translate = useTranslate();
  const [days, setDays] = useState(30);
  const { data, isPending } = useGetList<Lead>("leads", {
    // Tagesgenau, sonst ändert sich der Query-Key bei jedem Rendern
    filter: {
      "created_at@gte": startOfDay(subDays(new Date(), days)).toISOString(),
    },
    sort: { field: "created_at", order: "DESC" },
    pagination: { page: 1, perPage: 1000 },
  });

  const leads = data ?? [];
  const kpis = computeKpis(leads);

  return (
    <div className="flex flex-col gap-6 mt-2">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{translate("crm.kpi.title")}</h1>
        <div className="flex gap-1">
          {PERIODS.map((period) => (
            <Button
              key={period}
              size="sm"
              variant={days === period ? "default" : "outline"}
              onClick={() => setDays(period)}
            >
              {translate("crm.kpi.last_days", { days: period })}
            </Button>
          ))}
        </div>
      </div>

      {isPending ? null : (
        <>
          <LeadKpiTiles kpis={kpis} />
          <KpiTable
            title="crm.kpi.by_sales"
            fallbackLabel="resources.leads.fields.sales_id"
            rows={groupKpis(
              leads,
              (lead) => lead.sales_name,
              translate("crm.kpi.unassigned"),
            )}
          />
          <KpiTable
            title="crm.kpi.by_source"
            fallbackLabel="resources.leads.fields.source"
            rows={groupKpis(
              leads,
              (lead) => translate(`resources.leads.source.${lead.source}`),
              "–",
            )}
          />
          <KpiTable
            title="crm.kpi.by_campaign"
            fallbackLabel="resources.leads.fields.campaign"
            rows={groupKpis(leads, (lead) => lead.campaign, "–")}
          />
        </>
      )}
    </div>
  );
};

LeadKpiPage.path = "/kennzahlen";
